import {
  ComputeBudgetProgram,
  PublicKey,
  SystemProgram,
  TransactionInstruction,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js'
import { REFERRER_KEY, WRAPPED_SOL_MINT } from 'utils/constants'
import FormatNumericValue from '@components/shared/FormatNumericValue'
import { useCallback, useMemo } from 'react'
import { IconButton } from '@components/shared/Button'
import { CheckIcon, NoSymbolIcon } from '@heroicons/react/20/solid'
import { useSortableData } from 'hooks/useSortableData'
import { useQuery } from '@tanstack/react-query'
import { fetchMetadataForMints, toUiDecimals } from 'utils/tokens'
import ImageWithFallback from '@components/image/ImageWithFallback'
import SheenLoader from '@components/loading/SheenLoader'
import { useWallet } from '@solana/wallet-adapter-react'
import { Market, UiWrapper } from '@cks-systems/manifest-sdk'
import { createSettleFundsInstruction } from '@cks-systems/manifest-sdk/dist/cjs/ui_wrapper/instructions'
import { getVaultAddress } from '@cks-systems/manifest-sdk/dist/cjs/utils'
import { abbreviateAddress } from 'utils/formatting'
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountIdempotentInstruction,
  createCloseAccountInstruction,
  createSyncNativeInstruction,
  getAssociatedTokenAddressSync,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { sendTxAndConfirm, TransactionError } from 'utils/governance/tools'
import { MANIFEST_PROGRAM_ID } from 'utils/limitOrders'
import {
  getSimulationComputeUnits,
  isSetComputeLimitInstruction,
  PLATFORM_FEE_WALLET,
} from 'utils/referrals'
import store from 'stores/store'
import { notify } from 'utils/notifications'
import { useInvalidateWalletTokens } from 'hooks/useWalletTokens'
import { useUnsettledBalances } from 'hooks/useUnsettledBalances'
import { sleep } from 'utils'
import { useOpenOrders } from 'hooks/useOpenOrders'
import { getFeeEstimate } from 'utils/priorityFee'
import { isError } from 'lodash'
import { usePlausibleChecked } from 'hooks/usePlausibleChecked'
import { TokenMetadataItem } from 'types/token'
import { useTranslation } from 'next-i18next'
import { useReferrerFee } from '@components/partners/useReferrerFee'
import { milliseconds } from 'date-fns'

const metadataCache = new Map<string, TokenMetadataItem[]>()

const UnsettledTable = () => {
  const { publicKey, signTransaction } = useWallet()
  const connection = store((s) => s.connection)
  const [localReferrer] = useLocalStorage(REFERRER_KEY)
  const { unsettledBalances, refetchUnsettledBalances } = useUnsettledBalances()
  const invalidateWalletTokens = useInvalidateWalletTokens()
  const { refetchOpenOrders } = useOpenOrders()
  const plausible = usePlausibleChecked()
  const { t } = useTranslation(['orders', 'messages'])
  const { referrerFee } = useReferrerFee()

  const metaDataMints = useMemo(() => {
    try {
      if (!unsettledBalances?.length) return []
      const uniqueBaseMints = new Set(
        unsettledBalances.map((u) => u.market.baseMint().toBase58()),
      )
      const uniqueQuoteMints = new Set(
        unsettledBalances.map((u) => u.market.quoteMint().toBase58()),
      )

      const combinedMints = new Set([...uniqueBaseMints, ...uniqueQuoteMints])

      return Array.from(combinedMints)
    } catch (e) {
      console.error('Error calculating metaDataMints', e)
      return []
    }
  }, [unsettledBalances])

  const {
    data: unsettledTokensMeta,
    isInitialLoading: loadingUnsettledTokensMeta,
  } = useQuery({
    queryKey: ['unsettled-token-meta', metaDataMints],
    queryFn: () =>
      fetchMetadataForMints(metaDataMints, metadataCache, connection),
    cacheTime: milliseconds({ minutes: 30 }),
    staleTime: milliseconds({ minutes: 30 }),
    retry: 3,
    refetchOnWindowFocus: false,
    enabled: !!metaDataMints?.length,
  })

  const formattedTableData = useCallback(() => {
    if (!unsettledBalances?.length) return []
    const data = []
    for (let i = 0; i < unsettledBalances.length; i++) {
      const u = unsettledBalances[i]
      const abbreviateBaseMint = abbreviateAddress(u.market.baseMint())
      const abbreviateQuoteMint = abbreviateAddress(u.market.quoteMint())
      if (!u.numBaseTokens && !u.numQuoteTokens) continue

      if (unsettledTokensMeta && unsettledTokensMeta?.length) {
        const quoteMeta = unsettledTokensMeta.find(
          (meta) => meta.mint === u.market.quoteMint().toBase58(),
        )
        const baseMeta = unsettledTokensMeta.find(
          (meta) => meta.mint === u.market.baseMint().toBase58(),
        )

        const formattedUnsettled = {
          marketName: `${baseMeta?.symbol || abbreviateBaseMint}/${
            quoteMeta?.symbol || abbreviateQuoteMint
          }`,
          marketAddress: u.market.address.toBase58(),
          baseSymbol: baseMeta?.symbol || abbreviateBaseMint,
          quoteSymbol: quoteMeta?.symbol || abbreviateQuoteMint,
          imageUrl: baseMeta?.image_url || '',
          market: u.market,
          uiAmountBase: u.numBaseTokens,
          uiAmountQuote: u.numQuoteTokens,
          baseProgram: baseMeta?.token_program || TOKEN_PROGRAM_ID.toBase58(),
          quoteProgram: quoteMeta?.token_program || TOKEN_PROGRAM_ID.toBase58(),
        }
        data.push(formattedUnsettled)
      }
    }
    return data
  }, [unsettledBalances, unsettledTokensMeta])

  const { items: tableData } = useSortableData(formattedTableData())

  const settle = useCallback(
    async (
      market: Market,
      localReferrer: string | undefined,
      baseProgram: PublicKey,
      quoteProgram: PublicKey,
    ) => {
      if (!publicKey || !signTransaction) {
        console.warn("Can't settle order without a wallet")
        return
      }
      let txid: string | undefined
      const defaultComputeUnit = 340000
      try {
        const txFee = await getFeeEstimate(connection)
        let refTokenAccountPk = undefined
        const baseMint = market.baseMint()
        const quoteMint = market.quoteMint()
        const quoteMeta = unsettledTokensMeta?.find(
          (meta) => meta.mint === quoteMint.toBase58(),
        )
        const feeMintIsWSol = quoteMint.equals(WRAPPED_SOL_MINT)
        const hasRef = !!localReferrer
        let preSolWrapInstructions: TransactionInstruction[] = [
          ComputeBudgetProgram.setComputeUnitPrice({
            microLamports: txFee,
          }),
          ComputeBudgetProgram.setComputeUnitLimit({
            units: defaultComputeUnit,
          }),
        ]
        const postSolWrapInstructions: TransactionInstruction[] = []

        const wrapperAcc = await UiWrapper.fetchFirstUserWrapper(
          connection,
          publicKey,
        )
        if (!wrapperAcc) {
          console.error('No wrapper account found')
          notify({
            type: 'error',
            title: t('errors.no-wrapper'),
          })
          plausible('SettleOrderError', {
            props: {
              error: 'No wrapper account found',
              wallet: publicKey.toString(),
              market: market.toString(),
              baseMint: baseMint.toString(),
              quoteMint: quoteMint.toString(),
            },
          })
          return
        }

        const traderTokenAccountBase = getAssociatedTokenAddressSync(
          baseMint,
          publicKey,
          true,
          baseProgram,
        )
        const traderTokenAccountQuote = getAssociatedTokenAddressSync(
          quoteMint,
          publicKey,
          true,
          quoteProgram,
        )
        const platformAta = getAssociatedTokenAddressSync(
          quoteMint,
          PLATFORM_FEE_WALLET,
          true,
          quoteProgram,
        )
        const [platformAtaAccount, baseAtaAccount, quoteAtaAccount] =
          await Promise.all([
            connection.getAccountInfo(platformAta),
            connection.getAccountInfo(traderTokenAccountBase),
            connection.getAccountInfo(traderTokenAccountQuote),
          ])

        if (hasRef) {
          const [tokenAccountAddress] = PublicKey.findProgramAddressSync(
            [
              new PublicKey(localReferrer).toBuffer(),
              quoteProgram.toBuffer(),
              new PublicKey(quoteMint).toBuffer(),
            ],
            ASSOCIATED_TOKEN_PROGRAM_ID,
          )
          refTokenAccountPk = tokenAccountAddress
          const refVaultAccount =
            await connection.getAccountInfo(tokenAccountAddress)
          const refVaultExists = refVaultAccount && refVaultAccount.lamports > 0
          if (!refVaultExists) {
            const referrerVaultIx =
              createAssociatedTokenAccountIdempotentInstruction(
                publicKey,
                tokenAccountAddress,
                new PublicKey(localReferrer),
                quoteMint,
                quoteProgram,
              )

            preSolWrapInstructions.push(referrerVaultIx)
          }
        }

        const doesPlatformAtaExist =
          platformAtaAccount && platformAtaAccount.lamports > 0

        const doesBaseAtaExist = baseAtaAccount && baseAtaAccount.lamports > 0
        const doesQuoteAtaExist =
          quoteAtaAccount && quoteAtaAccount.lamports > 0

        if (!doesPlatformAtaExist) {
          const platformAtaCreateIx =
            createAssociatedTokenAccountIdempotentInstruction(
              publicKey,
              platformAta,
              PLATFORM_FEE_WALLET,
              quoteMint,
              quoteProgram,
            )

          preSolWrapInstructions.push(platformAtaCreateIx)
        }
        if (!doesQuoteAtaExist) {
          const quoteAtaCreateIx =
            createAssociatedTokenAccountIdempotentInstruction(
              publicKey,
              traderTokenAccountQuote,
              publicKey,
              quoteMint,
              quoteProgram,
            )

          preSolWrapInstructions.push(quoteAtaCreateIx)
        }
        if (!doesBaseAtaExist) {
          const baseAtaCreateIx =
            createAssociatedTokenAccountIdempotentInstruction(
              publicKey,
              traderTokenAccountBase,
              publicKey,
              baseMint,
              baseProgram,
            )

          preSolWrapInstructions.push(baseAtaCreateIx)
        }

        const settleOrderIx: TransactionInstruction =
          createSettleFundsInstruction(
            {
              wrapperState: wrapperAcc.pubkey,
              owner: publicKey,
              market: market.address,
              manifestProgram: MANIFEST_PROGRAM_ID,
              traderTokenAccountBase: traderTokenAccountBase,
              traderTokenAccountQuote: traderTokenAccountQuote,
              vaultBase: getVaultAddress(market.address, baseMint),
              vaultQuote: getVaultAddress(market.address, quoteMint),
              mintBase: baseMint,
              mintQuote: quoteMint,
              tokenProgramBase: baseProgram,
              tokenProgramQuote: quoteProgram,
              platformTokenAccount: platformAta,
              referrerTokenAccount: refTokenAccountPk,
            },
            {
              params: {
                feeMantissa: 10 ** 9 * (refTokenAccountPk ? 0.0009 : 0.001),
                platformFeePercent: 100 - referrerFee,
              },
            },
          )
        postSolWrapInstructions.push(settleOrderIx)
        if (baseMint.equals(WRAPPED_SOL_MINT)) {
          const solTransferIx = createCloseAccountInstruction(
            traderTokenAccountBase,
            publicKey,
            publicKey,
          )
          postSolWrapInstructions.push(solTransferIx)
        }
        if (quoteMint.equals(WRAPPED_SOL_MINT)) {
          const solTransferIx = createCloseAccountInstruction(
            traderTokenAccountQuote,
            publicKey,
            publicKey,
          )
          postSolWrapInstructions.push(solTransferIx)
        }

        const units = await getSimulationComputeUnits(
          connection,
          [...preSolWrapInstructions, ...postSolWrapInstructions],
          publicKey,
          [],
        )
        //replace compute unit with dynamic value
        preSolWrapInstructions = [
          ...preSolWrapInstructions.map((ix) => {
            if (isSetComputeLimitInstruction(ix)) {
              return ComputeBudgetProgram.setComputeUnitLimit({
                units: units || defaultComputeUnit,
              })
            }
            return ix
          }),
        ]
        const block = await connection.getLatestBlockhash('finalized')
        let vtx = new VersionedTransaction(
          new TransactionMessage({
            payerKey: publicKey,
            instructions: [
              ...preSolWrapInstructions,
              ...postSolWrapInstructions,
            ],
            recentBlockhash: block.blockhash,
          }).compileToV0Message(),
        )
        //TODO more errors handling generic
        const simulation = await connection.simulateTransaction(vtx)
        const simulationLogs = simulation.value.logs?.toString()

        if (
          simulation.value.err &&
          simulationLogs?.includes('Error: insufficient funds')
        ) {
          const feeAmount = parseInt(
            simulationLogs.match(/fee_atoms:(\d+)/)?.[1] || '0',
            10,
          )
          if (feeMintIsWSol) {
            const solTransferIx = SystemProgram.transfer({
              fromPubkey: publicKey,
              toPubkey: traderTokenAccountQuote,
              lamports: feeAmount,
            })
            const syncNative = createSyncNativeInstruction(
              traderTokenAccountQuote,
            )
            preSolWrapInstructions.push(solTransferIx, syncNative)
            const block = await connection.getLatestBlockhash('finalized')
            vtx = new VersionedTransaction(
              new TransactionMessage({
                payerKey: publicKey,
                instructions: [
                  ...preSolWrapInstructions,
                  ...postSolWrapInstructions,
                ],
                recentBlockhash: block.blockhash,
              }).compileToV0Message(),
            )
          } else {
            notify({
              type: 'error',
              title: t('errors.insufficient-funds', {
                amount: toUiDecimals(feeAmount, quoteMeta?.decimals || 6),
                symbol: quoteMeta?.symbol || quoteMint.toBase58(),
              }),
            })
            return
          }
        }

        const signed = await signTransaction(vtx)
        txid = await sendTxAndConfirm(connection, signed, block, {
          transactionSent: t('messages:transaction-sent'),
          waitingForConfirmation: t('messages:waiting-for-confirmation'),
        })

        notify({
          title: t('messages:transaction-confirmed'),
          type: 'success',
          txid,
        })
        await sleep(1000)
        void invalidateWalletTokens()
        void refetchOpenOrders()
        void refetchUnsettledBalances()
      } catch (e: unknown) {
        console.error(`error settling order: txid=${txid}`, e)
        notify({
          title: t('messages:errors.transaction-error'),
          description:
            e != null
              ? e instanceof TransactionError || isError(e)
                ? e.message
                : String(e)
              : t('messages:errors.general-try-again'),
          type: 'error',
          txid:
            txid != null ? txid : e instanceof TransactionError ? e.txid : '',
        })
        plausible('SettleOrderError', {
          props: {
            error: String(e),
            wallet: String(publicKey),
            txid,
            market: market.address.toString(),
            baseMint: market.baseMint().toString(),
            quoteMint: market.quoteMint().toString(),
          },
        })
      }
    },
    [
      connection,
      invalidateWalletTokens,
      plausible,
      publicKey,
      referrerFee,
      refetchOpenOrders,
      refetchUnsettledBalances,
      signTransaction,
      t,
      unsettledTokensMeta,
    ],
  )

  return tableData?.length ? (
    <div>
      <p className="mb-4 text-xs">{t('table.settle-description')}</p>
      <div className="space-y-3">
        {tableData.map((data, index) => {
          const {
            marketName,
            marketAddress,
            imageUrl,
            baseSymbol,
            quoteSymbol,
            uiAmountBase,
            uiAmountQuote,
            market,
            quoteProgram,
            baseProgram,
          } = data

          return (
            <div
              key={`${marketAddress}${uiAmountBase}${index}`}
              className="flex items-center justify-between"
            >
              <div>
                {loadingUnsettledTokensMeta ? (
                  <SheenLoader>
                    <div className="h-4 w-24 bg-th-bkg-2" />
                  </SheenLoader>
                ) : (
                  <div className="flex items-center space-x-2">
                    <ImageWithFallback
                      src={imageUrl}
                      height={24}
                      width={24}
                      alt="Token logo"
                    />
                    <div>
                      <p className="font-body text-xs text-th-fgd-1 xl:text-sm">
                        {marketName}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-3 text-xs text-th-fgd-1 xl:text-sm">
                <div>
                  <FormatNumericValue value={uiAmountBase} />
                  <span className="ml-1.5 font-body text-xs text-th-fgd-3">
                    {baseSymbol}
                  </span>
                </div>
                <div>
                  <FormatNumericValue value={uiAmountQuote} />
                  <span className="ml-1.5 font-body text-xs text-th-fgd-3">
                    {quoteSymbol}
                  </span>
                </div>
                <div className="flex justify-end">
                  <IconButton
                    className="bg-th-up !text-th-button-text md:hover:bg-th-up-dark"
                    onClick={() =>
                      settle(
                        market,
                        localReferrer?.wallet,
                        new PublicKey(baseProgram),
                        new PublicKey(quoteProgram),
                      )
                    }
                    size="small"
                  >
                    <CheckIcon className="size-4" />
                  </IconButton>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  ) : (
    <div className="flex items-center justify-center rounded-xl border border-th-bkg-4 p-6">
      <div className="flex flex-col items-center">
        <NoSymbolIcon className="mb-2 size-5 text-th-fgd-4" />
        <p className="mb-1">{t('table.no-unsettled')}</p>
      </div>
    </div>
  )
}

export default UnsettledTable
